import React, { useState, useEffect, useRef } from 'react';
import { FormContext, useForm } from 'react-hook-form';
import { Helmet } from 'react-helmet';
import * as yup from 'yup';

import SubmitButton from './global/SubmitButton';
import Input from './global/Input';
import Breadcrumb from './global/Breadcrumb';
import GridContainer from './global/GridContainer';
import RadioButtonGroup from './global/RadioButtonGroup';
import RadioButton from './global/RadioButton';
import Select from './global/Select';
import Fieldset from './global/Fieldset';

import H1 from './../typography/H1';
import Legend from './../typography/Legend';
import Anchor from './../typography/Anchor';

import Response from './Response';
import TransactionField from './global/TransactionField';

import BaseTransition from '../transitions/BaseTransition';

import useElementSize from '../hooks/useElementSize';

import { trackPromise } from '../promise-tracker';

import Captcha from '../captcha/CaptchaWrapper';
import { SITE_KEY } from '../const/Const';

import { stripDollarSign } from '../../utils';

function PostpaidOneTimeTopup() {
  let search = null;
  let transDate = new Date();

  if (typeof window !== 'undefined') {
    search = window.location.search;
  }
  const params = new URLSearchParams(search);
  const ref2 = params.get('ref2');
  const ordPhoneNumber = params.get('ordPhoneNumber');
  const balance = params.get('balance');
  let topUpAmount = '0';
  if (ref2 === 'Basic10') topUpAmount = '10';
  else if (ref2 === 'Basic20') topUpAmount = '20';
  else if (ref2 === 'Basic50') topUpAmount = '50';
  else if (ref2 === 'Basic100') topUpAmount = '100';
  else topUpAmount = null;

  let noTopUpInfo;
  if (balance === null || ordPhoneNumber === null) noTopUpInfo = true;
  else noTopUpInfo = false;

  const [isCreditCard, setIsCreditCard] = useState(true);
  const [APICall, setAPICall] = useState(false);
  const [paymentResponse, setPaymentResponse] = useState({});
  const [interacResponse, setInteracResponse] = useState({});
  const [tooltips, setTooltips] = useState({});

  const schema = yup.object().shape({
    ordTopUpAmount: yup
      .string()
      .required()
      .test('invalid-amount', `Amount must have no more than two decimal digits and must be between $5.00 and $${balance} `, amount => {
        const transformedAmount = stripDollarSign(amount);
        return /^\d+(\.\d{1,2})?$/.test(transformedAmount) && Number.parseFloat(transformedAmount) >= 5 && Number.parseFloat(transformedAmount) <= balance;
      }),
    ordPhoneNumber: yup.string(),
    ordFirstName: yup.string().required(),
    ordLastName: yup.string().required(),
    ordEmailAddress: yup
      .string()
      .email()
      .required(),
    ordAddress1: yup.string().required(),
    ordCity: yup.string().required(),
    ordPostalCode: yup
      .string()
      .required()
      .matches(/[a-zA-Z][0-9][a-zA-Z](-| |)[0-9][a-zA-Z][0-9]/),
    trnCardNumber: yup
      .string()
      .required()
      .matches(
        /^(?:4[0-9]{12}(?:[0-9]{3})?|5[1-5][0-9]{14}|6(?:011|5[0-9][0-9])[0-9]{12}|3[47][0-9]{13}|3(?:0[0-5]|[68][0-9])[0-9]{11}|(?:2131|1800|35\d{3})\d{11})$/
      ),
    trnExpirationDate: yup
      .string()
      .required()
      .matches(/^(0[1-9]|1[0-2])\/?([0-9]{2})$/),
    trnCardCvd: yup
      .string()
      .required()
      .matches(/^([0-9]){3,4}$/),
  });

  let methods = useForm({
    validationSchema: schema,
    mode: 'onBlur',
    reValidateMode: 'onChange',
    submitFocusError: true,
    defaultValues: {
      ordTopUpAmount: '$',
    },
  });
  const recaptchaRef = useRef(null);
  const formRef = useRef(null);
  useEffect(() => {
    register({ name: 'captchaToken' }, { required: true });
  });

  useEffect(() => {
    // just a workaround to ensure that boundary element in the tooltip popperOptions is already in the DOM
    // TO-DO: find a better solution
    setTooltips({
      topupAmount: `Must be a dollar amount between $5 and \$${balance ? balance.split('.')[0] : ''}.`,
      firstName: 'Must be entered exactly as it appears on your credit card.',
      email: 'For purchase confirmation. Will not be shared with any other company.',
      expiration: 'Must not be within 2 months.',
      cvv: 'Last 3 digits on the back of your credit card.',
    });
  }, []);

  const { errors, setValue, register, triggerValidation } = { ...methods };

  const [ref, sizes] = useElementSize(true);

  console.log(errors);
  // console.log(sizes);

  const handleClick = async () => {
    const isValid = await triggerValidation();
    if (isValid) {
      recaptchaRef.current.execute();
    } else {
      // still try to submit the form since RHF won't allow that but nevertheless will focus into the first invalid field
      formRef.current.dispatchEvent(new Event('submit', { cancelable: true }));
    }
  };

  const onSubmit = data => {
    console.log(data);

    const token = {
      number: data.trnCardNumber,
      expiry_month: data.trnExpirationDate.slice(0, 2),
      expiry_year: data.trnExpirationDate.slice(-2),
      cvd: data.trnCardCvd,
    };

    trackPromise(
      fetch('https://api.na.bambora.com/scripts/tokenization/tokens', {
        method: 'post',
        headers: {
          'Content-type': 'application/x-www-form-urlencoded; charset=UTF-8',
        },
        body: JSON.stringify(token),
      })
    )
      .then(response => response.json())
      .then(response => {
        const ordAmount = topUpAmount ? topUpAmount : stripDollarSign(data.ordTopUpAmount);
        const dateStamp = new Date();

        console.log('Tokenize request succeeded with JSON response', response);

        trackPromise(
          fetch('/api/payment/postpaid', {
            // fetch(`http://localhost:9080/api/payment/prepaid`, {
            // fetch('https://api.na.bambora.com/v1/payments', {
            method: 'post',
            headers: {
              'Content-Type': 'application/json',
              // Authorization:
              //   // 'Passcode ' + btoa('191400000:08F85139625f416E95B851C292AE4AEC'),
              //   'Passcode ' +
              //   btoa('300208596:a799c647cbdD4589B0F0602e6aE3ACB3'),
            },
            body: JSON.stringify({
              order_number: `${ordPhoneNumber}${dateStamp.getMonth() +
                1}${dateStamp.getDay()}${dateStamp.getFullYear()}${dateStamp.getHours()}${dateStamp.getMinutes()}${dateStamp.getSeconds()}`,
              amount: ordAmount,
              payment_method: 'token',
              billing: {
                name: `${data.ordFirstName} ${data.ordLastName}`,
                address_line1: data.ordAddress1,
                address_line2: data.ordAddress2,
                city: data.ordCity,
                province: data.ordProvince,
                country: 'CA',
                postal_code: data.ordPostalCode,
                phone_number: ordPhoneNumber,
                email_address: data.ordEmailAddress,
              },
              custom: {
                ref1: ordPhoneNumber,
                ref2: 'Post-paid',
                ref3: 'SMS,Email',
                ref4: 0,
              },
              token: {
                complete: true,
                code: response.token,
                name: `${data.ordFirstName} ${data.ordLastName}`,
              },
              captchaToken: data.captchaToken,
            }),
          })
        )
          .then(response => response.json())
          .then(response => {
            setPaymentResponse(response.data);
            setAPICall(true);
            console.log(paymentResponse);
          })
          .catch(error => {
            console.log('Request failed', error);
          });
      })
      .catch(error => {
        console.log('Request failed', error);
      });
  };

  const onChangePaymentMethod = () => {
    setIsCreditCard(!isCreditCard);
  };

  const onCapthcaChange = token => {
    setValue('captchaToken', token);
    if (token) {
      formRef.current.dispatchEvent(new Event('submit', { cancelable: true }));
    }
  };

  return (
    <main className="flex-auto">
      <Helmet
        htmlAttributes={{
          lang: 'en',
        }}
      >
        <title>SaskTel.com | Spending Limit Top‑up Form</title>
      </Helmet>
      {Object.keys(paymentResponse).length === 0 && paymentResponse.constructor === Object && (
        <div className="flex flex-col font-museo font-w-300">
          <div className="flex-shrink md:px-30 lg:px-45 mx-auto my-5 md:my-0 max-w-w-7xl w-full">
            <Breadcrumb
              breadcrumbs={[
                {
                  label: 'Home',
                  link: 'https://www.sasktel.com/wps/wcm/connect/content/home/',
                },
                {
                  label: 'Wireless',
                  link: 'https://www.sasktel.com/wps/wcm/connect/content/home/wireless/',
                },
                {
                  label: 'Spending Limit Top-Up',
                  link: 'https://www.sasktel.com/wps/wcm/connect/content/home/forms/spending-limit-top-up',
                },
              ]}
            />
            <H1>Spending Limit Top‑up</H1>
          </div>
          <div className="flex-auto border-t-1 border-light-grey">
            <section className="pt-30 pb-50 px-15 md:py-60 md:px-30 lg:py-60 lg:px-0 max-w-w-3xl mx-auto">
              <FormContext {...methods}>
                <form onSubmit={methods.handleSubmit(onSubmit)} ref={formRef} classes="flex flex-col" id="form">
                  <Fieldset>
                    <Legend>Top-up Information</Legend>
                    <div className="w-auto m-0 p-0">
                      <div className="pl-5 pr-5 md:pl-0 md:pr-15 pb-30 w-full md:w-1\/2 m-0">
                        <label htmlFor="ordPhoneNumber" className="text-12 leading-12 tracking-1 font-mont-700 font-w-400 uppercase">
                          Wireless phone number
                        </label>
                        <p className="text-17 leading-22 font-w-400 tealeaf-mask">{noTopUpInfo ? 'None' : ordPhoneNumber}</p>
                      </div>
                    </div>
                    <div className="w-auto m-0 p-0">
                      <div className="pb-30">
                        <GridContainer col="6" gap="30" mdcol="12" mdgap="30">
                          <Input
                            prefix="$"
                            spacingClasses="col-span-6 pb-30"
                            label="Top-up Amount"
                            for="ordTopUpAmount"
                            tooltip={tooltips.topupAmount}
                            tooltipName="topup-tooltip"
                            error={errors.ordTopUpAmount}
                            caption={`Your subscription balance is: \$${noTopUpInfo ? 'None' : balance}`}
                            inputMode="numeric"
                          />
                        </GridContainer>

                        {/* <p className="pl-5 text-14 leading-18 font-w-400 h-35 text-dark-grey">
                            Your subscription balance is: $
                            {noTopUpInfo ? 'None' : balance}
                          </p> */}
                      </div>
                    </div>
                  </Fieldset>

                  <Fieldset classes="pt-30 pb-0 md:pt-40 md:pb-10">
                    <Legend>Billing Address Information</Legend>
                    <GridContainer col="6" gap="0" mdcol="12" mdgap="30">
                      <Input
                        spacingClasses="col-span-6 pb-30"
                        label="First name"
                        for="ordFirstName"
                        tooltip={tooltips.firstName}
                        tooltipName="first-name-tooltip"
                        error={errors.ordFirstName}
                      />
                      <Input spacingClasses="col-span-6 pb-30" label="Last name" for="ordLastName" error={errors.ordLastName} />
                      <Input
                        spacingClasses="col-span-6 pb-30"
                        label="Email"
                        for="ordEmailAddress"
                        tooltip={tooltips.email}
                        tooltipName="email-tooltip"
                        error={errors.ordEmailAddress}
                      />
                      <Input spacingClasses="col-start-1 col-end-7 pb-30" label="Address line 1" for="ordAddress1" error={errors.ordAddress1} />
                      <Input optional spacingClasses="col-span-6 pb-30" label="Address line 2" for="ordAddress2" />
                      <Input spacingClasses="col-span-6 pb-30" label="City / Town" for="ordCity" error={errors.ordCity} />
                      <Select
                        spacingClasses="col-span-6 md:col-span-3"
                        label="Province"
                        name="ordProvince"
                        for="ordProvince"
                        id="ordProvince"
                        options={['AB', 'BC', 'MB', 'NB', 'NL', 'NS', 'NT', 'NU', 'ON', 'PE', 'QC', 'SK', 'YT']}
                        defaultValue="SK"
                      />
                      <Input
                        placeholder="A5A 5A5"
                        spacingClasses="col-span-6 md:col-span-3 pb-30"
                        label="Postal code"
                        mask="a9a 9a9"
                        for="ordPostalCode"
                        error={errors.ordPostalCode}
                      />
                    </GridContainer>
                  </Fieldset>
                  <Fieldset classes="py-30 md:py-40">
                    <Legend>Payment Information</Legend>

                    <GridContainer col="6" gap="0" mdcol="12" mdgap="30">
                      <Input
                        placeholder="1234 5678 9087 6543"
                        spacingClasses="col-span-6 pb-30"
                        label="Card number"
                        for="trnCardNumber"
                        error={errors.trnCardNumber}
                      />
                      <Input
                        placeholder="MM/YY"
                        spacingClasses="col-span-6 md:col-span-3 pb-30"
                        label="Expiration date"
                        for="trnExpirationDate"
                        mask="99\/99"
                        tooltip={tooltips.expiration}
                        tooltipName="expiration-tooltip"
                        error={errors.trnExpirationDate}
                      />
                      <Input
                        spacingClasses="col-span-6 md:col-span-3 pb-30"
                        label="CVV"
                        length="4"
                        for="trnCardCvd"
                        tooltip={tooltips.cvv}
                        tooltipName="cvv-tooltip"
                        error={errors.trnCardCvd}
                      />
                    </GridContainer>

                    <div className="row mt-5 md:mt-0">
                      <div className="px-5 md:px-0 pb-30 w-auto large-12 columns">
                        <div className="font-museo font-w-300 leading-18 text-14">
                          This service is for existing SaskTel wireless customers only. Recurring billing by credit card is available for{' '}
                          <Anchor href="https://www.visa.com/" aria="This link will take you to www.visa.com">
                            Visa
                          </Anchor>
                          ,{' '}
                          <Anchor href="https://www.americanexpress.com/" aria="This link will take you to www.americanexpress.com">
                            American Express
                          </Anchor>{' '}
                          and{' '}
                          <Anchor href="https://www.mastercard.com/" aria="This link will take you to www.mastercard.com">
                            Mastercard
                          </Anchor>
                          .
                        </div>
                      </div>
                    </div>
                    <Captcha theme="light" ref={recaptchaRef} sitekey={SITE_KEY} size={'invisible'} badge={'inline'} onChange={onCapthcaChange} />
                  </Fieldset>
                  <Fieldset custom classes="pt-30 md:pt-40 flex flex-col">
                    <div className="w-auto m-0 p-0 flex flex-row">
                      <SubmitButton value="Submit" type="button" name="submit" buttonType="primary" classes="m-auto" click={handleClick}></SubmitButton>
                    </div>
                  </Fieldset>
                </form>
              </FormContext>
            </section>
          </div>
        </div>
      )}
      <div className="flex flex-col font-museo font-w-300" role="status">
        {paymentResponse.approved && paymentResponse.approved === '1' && (
          <Response
            name="paymentMethod"
            label="Payment Method"
            isApproved={true}
            breadCrumb={[
              {
                label: 'Home',
                link: 'https://www.sasktel.com/wps/wcm/connect/content/home/',
              },
              {
                label: 'Wireless',
                link: 'https://www.sasktel.com/wps/wcm/connect/content/home/wireless/',
              },
              {
                label: 'noStrings Prepaid',
                link: 'https://www.sasktel.com/wps/wcm/connect/content/home/wireless/prepaid/',
              },
            ]}
            header="Transaction Approved"
            buttonValue="back"
            buttonOnClick={() => window.history.back()}
          >
            <div className="font-museo font-w-300 leading-22 text-17 mb-50">
              Thanks for your order! You'll receive a confirmation text and email when your account is topped up.
            </div>
            <TransactionField label="Status" content="Approved" />
            <TransactionField label="Transaction ID" content={paymentResponse.order_number} sensitive />
            <TransactionField label="Authorization code" content={paymentResponse.auth_code} />
            <TransactionField label="Billed amount" content={`\$${paymentResponse.amount.toFixed(2)} (including taxes)`} />
            <TransactionField label="Applied to SaskTel wireless phone number" content={paymentResponse.custom.ref1} sensitive />
            <TransactionField label="Transaction date" content={`${transDate.toLocaleDateString()} ${transDate.toLocaleTimeString()}`} />
          </Response>
        )}
        {paymentResponse.message !== 'ERROR' && !paymentResponse.approved && APICall && (
          <Response
            name="paymentMethod"
            label="Payment Method"
            isApproved={false}
            breadCrumb={[
              {
                label: 'Home',
                link: 'https://www.sasktel.com/wps/wcm/connect/content/home/',
              },
              {
                label: 'Wireless',
                link: 'https://www.sasktel.com/wps/wcm/connect/content/home/wireless/',
              },
              {
                label: 'noStrings Prepaid',
                link: 'https://www.sasktel.com/wps/wcm/connect/content/home/wireless/prepaid/',
              },
            ]}
            header="Transaction Declined"
            buttonValue="back"
            buttonOnClick={() => window.history.back()}
          >
            <div className="w-auto m-0 p-0 mb-50 text-center">
              <p className="mb-15 md:mb-20">Your transaction has been declined.</p>
              <p className="mb-0">
                Please make sure you've entered your card details and personal information correctly. If the problem persists, we suggest trying another credit
                card or contacting your financial institution.
              </p>
            </div>
            {/* <TransactionField label="Transaction ID" content="10103342" />
            <TransactionField label="Status" content="Invalid Card Number" /> */}
            <TransactionField label="Transaction date" content={`${transDate.toLocaleDateString()} ${transDate.toLocaleTimeString()}`} />
          </Response>
        )}
        {paymentResponse.message === 'ERROR' && (
          <Response
            name="paymentMethod"
            label="Payment Method"
            isApproved={false}
            breadCrumb={[
              {
                label: 'Home',
                link: 'https://www.sasktel.com/wps/wcm/connect/content/home/',
              },
              {
                label: 'Wireless',
                link: 'https://www.sasktel.com/wps/wcm/connect/content/home/wireless/',
              },
              {
                label: 'noStrings Prepaid',
                link: 'https://www.sasktel.com/wps/wcm/connect/content/home/wireless/prepaid/',
              },
            ]}
            header="Submission Error"
            buttonValue="back"
            buttonOnClick={() => window.history.back()}
          >
            <div className="flex flex-col items-center mb-15 md:mb-20 text-17 leading-22">
              <p className="mb-0 text-center">Your submission could not be processed due to the following error(s):</p>
              <ul className="mb-0 mt-10 list-disc list-inside mx-auto flex flex-col items-start">
                <li className="mt-10">Invalid billing period</li>
                <li className="mt-10">Invalid billing interval</li>
                <li className="mt-10">Invalid first billing date</li>
              </ul>
            </div>
          </Response>
        )}
      </div>
      <div
        className="hidden"
        dangerouslySetInnerHTML={{
          __html: unescape(interacResponse).replace(/\+/gm, ' '),
        }}
      />
    </main>
  );
}
export default PostpaidOneTimeTopup;
